/** Wren canonical JSON v1: recursive Unicode-scalar-sorted keys, compact UTF-8,
 * array order retained, finite numbers in plain decimal with no trailing zeroes.
 * Postgres implementation: canonical_payload_json in migration 20260912141000.
 * Unlike jsonb::text this encoding is independent of key length and whitespace.
 */
export async function payloadHash(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalStringify(value))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
}

function scalarOrder(a: string, b: string): number {
  const x = Array.from(a), y = Array.from(b)
  for (let i = 0; i < Math.min(x.length, y.length); i++) {
    const diff = x[i].codePointAt(0)! - y[i].codePointAt(0)!
    if (diff) return diff
  }
  return x.length - y.length
}
function decimal(value: number): string {
  if (!Number.isFinite(value)) throw new Error('non_json_number')
  const text = String(value) // Includes normalization of -0.
  if (!text.includes('e')) return text
  const [mantissa, exponent] = text.split('e')
  const sign = mantissa.startsWith('-') ? '-' : ''
  const unsigned = mantissa.replace('-', '')
  const digits = unsigned.replace('.', '')
  const point = (unsigned.split('.')[0].length) + Number(exponent)
  if (point <= 0) return sign + '0.' + '0'.repeat(-point) + digits
  if (point >= digits.length) return sign + digits + '0'.repeat(point - digits.length)
  return sign + digits.slice(0, point) + '.' + digits.slice(point)
}
export function canonicalStringify(value: unknown): string {
  if (typeof value === 'number') return decimal(value)
  if (value === null || typeof value === 'boolean') return JSON.stringify(value)
  if (typeof value === 'string') {
    // Postgres UTF-8 JSONB rejects NUL and unpaired surrogates too.
    if (/\u0000|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u.test(value)) throw new Error('non_json_string')
    return JSON.stringify(value)
  }
  if (Array.isArray(value)) return `[${Array.from(value, canonicalStringify).join(',')}]`
  if (typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) throw new Error('non_json_value')
  const object = value as Record<string, unknown>
  return `{${Object.keys(object).sort(scalarOrder).map(k => `${canonicalStringify(k)}:${canonicalStringify(object[k])}`).join(',')}}`
}

/** Recognize only known encodings, not arbitrary unequal digests. PostgreSQL's
 * legacy JSONB format sorts keys by UTF-8 byte length, then byte order.
 * Exotic legacy SQL numeric scales cannot be recovered from JS JSON parsing:
 * reject those hashes rather than waiving INV-7; investigate at the live gate.
 */
function historicalJson(value: unknown, postgres: boolean): string {
  if (Array.isArray(value)) return `[${value.map(v => historicalJson(v, postgres)).join(postgres ? ', ' : ',')}]`
  if (value === null || typeof value !== 'object') return postgres ? canonicalStringify(value) : JSON.stringify(value)
  const obj = value as Record<string, unknown>
  const keys = Object.keys(obj).sort(postgres
    ? (a, b) => new TextEncoder().encode(a).length - new TextEncoder().encode(b).length || scalarOrder(a, b)
    : undefined)
  return `{${keys.map(k => `${JSON.stringify(k)}:${postgres ? ' ' : ''}${historicalJson(obj[k], postgres)}`).join(postgres ? ', ' : ',')}}`
}
export async function compatiblePayloads(localJson: string, remote: unknown, localHash: string, remoteHash: string): Promise<boolean> {
  const local = JSON.parse(localJson)
  if (canonicalStringify(local) !== canonicalStringify(remote)) return false
  if (localHash === remoteHash) return true
  const encodings = [canonicalStringify(remote), historicalJson(remote, false), historicalJson(remote, true)]
  const hashes = await Promise.all(encodings.map(async text => {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
  }))
  return hashes.includes(localHash) && hashes.includes(remoteHash)
}
